import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import {
  formatCompactViews,
  formatDuration,
  formatShortDate,
  getCategoryName,
} from "@/utils/video";

interface PopularSectionProps {
  items: Video[];
}

export default function PopularSection({ items }: PopularSectionProps) {
  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-background-700 px-6 py-12 text-center text-sm text-foreground-500">
        热门榜单正在整理中，稍后再来看看吧。
      </p>
    );
  }

  const [spotlight, ...rest] = items;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <Link
        to={`/detail/${spotlight.id}`}
        className="group relative col-span-1 min-h-[320px] overflow-hidden rounded-lg border border-background-800 bg-background-900 lg:col-span-2 lg:min-h-[430px]"
      >
        <div className="relative aspect-video w-full overflow-hidden lg:absolute lg:inset-0 lg:aspect-auto lg:h-full">
          <img
            src={spotlight.cover}
            alt={`${spotlight.title} 封面`}
            title={`${spotlight.title} - 亚洲激情视频热门推荐`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 lg:absolute lg:inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/50 to-background-950/10" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-0.5 text-[11px] font-medium text-background-950">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-fire-fill text-[11px]" />
            </span>
            本站热度第一
          </span>
          <h3 className="mt-3 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
            {spotlight.title}
          </h3>
          <p className="mt-2 line-clamp-2 max-w-sm text-sm leading-relaxed text-foreground-300">
            {spotlight.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-400">
            <span className="text-primary-300">{getCategoryName(spotlight.category)}</span>
            <span className="tabular-nums">{formatDuration(spotlight.duration)}</span>
            <span className="tabular-nums">{formatCompactViews(spotlight.views)} 次播放</span>
          </div>
        </div>
      </Link>

      <div className="col-span-1 overflow-hidden rounded-lg border border-background-800 bg-background-900 lg:col-span-3">
        {rest.map((video, i) => (
          <Link
            key={video.id}
            to={`/detail/${video.id}`}
            className="group flex items-center gap-4 border-b border-background-800/80 p-4 last:border-b-0 hover:bg-background-800/60"
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded font-heading text-sm font-bold ${
                i === 0
                  ? "bg-accent-500 text-background-950"
                  : "bg-background-800 text-foreground-400"
              }`}
            >
              {String(i + 2).padStart(2, "0")}
            </span>

            <div className="relative h-[64px] w-[112px] shrink-0 overflow-hidden rounded bg-background-800 sm:h-[72px] sm:w-[128px]">
              <img
                src={video.cover}
                alt={`${video.title} 封面`}
                title={`${video.title} - 亚洲激情视频热门推荐`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute bottom-1 right-1 rounded bg-background-950/80 px-1 py-0.5 text-[10px] tabular-nums text-foreground-100">
                {formatDuration(video.duration)}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-200">
                {video.title}
              </h4>
              <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{video.summary}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                <span className="text-secondary-300">{getCategoryName(video.category)}</span>
                <span className="tabular-nums">{formatShortDate(video.publishedAt)}</span>
                <span className="tabular-nums">{formatCompactViews(video.views)} 次播放</span>
              </div>
            </div>

            <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full border border-background-700 text-foreground-500 transition-colors group-hover:border-primary-600 group-hover:text-primary-300 sm:flex">
              <i className="ri-play-fill text-sm" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}